import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  persistPin, readScope, readViewUid, resolveActiveUid, restorePin, SCOPE_AFTER_PIN, Scope, withoutPilotParam, writeScope,
} from "./lensState";

export interface Lens {
  activeUid: string | null;
  pinnedUid: string | null;
  viewUid: string | null;
  scope: Scope;
  notice: string;
  setScope: (scope: Scope) => void;
  clearViewUid: () => void;
  pin: (uid: string) => void;
  unpin: () => void;
}

const LensContext = createContext<Lens | null>(null);

export function LensProvider({ children }: { children: ReactNode }) {
  const [pin, setPin] = useState(restorePin);
  const [scope, setScopeState] = useState<Scope>(() => readScope() ?? (pin.uid ? SCOPE_AFTER_PIN : "everyone"));
  const [viewUid, setViewUid] = useState(() => readViewUid(window.location.hash));

  useEffect(() => {
    const onHashChange = () => setViewUid(readViewUid(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const setScope = useCallback((next: Scope) => {
    writeScope(next);
    setScopeState(next);
    // A URL pinned to the visitor's own pilot would otherwise override the choice just made.
    if (viewUid && viewUid === pin.uid) {
      window.history.replaceState(null, "", withoutPilotParam(window.location.hash));
      setViewUid(null);
    }
  }, [viewUid, pin.uid]);

  // Drops a pilot being viewed through the URL without touching the pin or the stored scope.
  const clearViewUid = useCallback(() => {
    window.history.replaceState(null, "", withoutPilotParam(window.location.hash));
    setViewUid(null);
  }, []);

  const pinPilot = useCallback((uid: string) => {
    setPin({ uid, notice: persistPin(uid) });
    setScope(SCOPE_AFTER_PIN);
  }, [setScope]);

  const unpin = useCallback(() => {
    setPin({ uid: null, notice: persistPin(null) });
    setScope("everyone");
  }, [setScope]);

  const value = useMemo<Lens>(() => ({
    activeUid: resolveActiveUid({ viewUid, pinnedUid: pin.uid, scope }),
    pinnedUid: pin.uid,
    viewUid,
    scope,
    notice: pin.notice,
    setScope,
    clearViewUid,
    pin: pinPilot,
    unpin,
  }), [viewUid, pin, scope, setScope, clearViewUid, pinPilot, unpin]);

  return <LensContext.Provider value={value}>{children}</LensContext.Provider>;
}

export function useLens(): Lens {
  const lens = useContext(LensContext);
  if (!lens) throw new Error("useLens must be used within LensProvider");
  return lens;
}
